import { useState } from "react";
import { toast } from "sonner";
import { Check, Send } from "lucide-react";
import api from "@/lib/api";
import { Reveal, HeroLine, FadeIn } from "@/components/Reveal";
import { inputCls, Label, GoldButton } from "@/components/Field";
import { usePageTitle } from "@/hooks/use-page-title";

const Eyebrow = ({ children }) => (
  <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-aspira-gold">{children}</p>
);

const OfferForm = () => {
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", state: "", county: "",
    parcel_number: "", acreage: "", location: "", details: "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads/sell", form);
      toast.success("Request received", {
        description: "Thanks — we'll review your property and be in touch. A confirmation is on its way to your email.",
      });
      setForm({ name: "", email: "", phone: "", state: "", county: "", parcel_number: "", acreage: "", location: "", details: "" });
    } catch (err) {
      toast.error("Something went wrong", { description: "Please try again in a moment." });
    } finally {
      setSending(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="sell-offer-form"
      className="rounded-3xl border border-aspira-goldborder bg-white p-7 shadow-xl sm:p-9"
    >
      <h2 className="font-serif text-2xl font-medium text-aspira-ink">Tell us about your property</h2>
      <p className="mt-2 text-sm text-aspira-muted">A few basic details is all we need to get started.</p>
      <div className="mt-7 grid gap-5 sm:grid-cols-2">
        <div>
          <Label htmlFor="sell-name">Full name *</Label>
          <input id="sell-name" data-testid="sell-name" required className={inputCls} placeholder="Jane Doe" value={form.name} onChange={set("name")} />
        </div>
        <div>
          <Label htmlFor="sell-email">Email address *</Label>
          <input id="sell-email" data-testid="sell-email" required type="email" className={inputCls} placeholder="you@email.com" value={form.email} onChange={set("email")} />
        </div>
        <div>
          <Label htmlFor="sell-phone">Phone number *</Label>
          <input id="sell-phone" data-testid="sell-phone" required className={inputCls} placeholder="(555) 123-4567" value={form.phone} onChange={set("phone")} />
        </div>
        <div>
          <Label htmlFor="sell-state">Property state *</Label>
          <input id="sell-state" data-testid="sell-state" required className={inputCls} placeholder="e.g. Texas" value={form.state} onChange={set("state")} />
        </div>
        <div>
          <Label htmlFor="sell-county">Property county</Label>
          <input id="sell-county" data-testid="sell-county" className={inputCls} placeholder="e.g. Brewster" value={form.county} onChange={set("county")} />
        </div>
        <div>
          <Label htmlFor="sell-parcel">Parcel number (if available)</Label>
          <input id="sell-parcel" data-testid="sell-parcel" className={inputCls} placeholder="Assessor parcel number" value={form.parcel_number} onChange={set("parcel_number")} />
        </div>
        <div>
          <Label htmlFor="sell-acreage">Approximate acreage</Label>
          <input id="sell-acreage" data-testid="sell-acreage" className={inputCls} placeholder="e.g. 5.2 acres" value={form.acreage} onChange={set("acreage")} />
        </div>
        <div>
          <Label htmlFor="sell-location">Property address or general location</Label>
          <input id="sell-location" data-testid="sell-location" className={inputCls} placeholder="Street address or nearest town" value={form.location} onChange={set("location")} />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="sell-details">Additional property details</Label>
          <textarea id="sell-details" data-testid="sell-details" rows={4} className={inputCls} placeholder="Anything else that would help us understand the property…" value={form.details} onChange={set("details")} />
        </div>
      </div>
      <GoldButton type="submit" data-testid="sell-form-submit" disabled={sending} className="mt-8 w-full sm:w-auto">
        {sending ? "Sending…" : "Request My Cash Offer"} <Send size={15} />
      </GoldButton>
      <p className="mt-4 text-xs text-aspira-muted">
        No pressure, no obligation. Your information is only used to review your property and respond to you.
      </p>
    </form>
  );
};

const STEPS = [
  ["Tell Us About Your Property", "Submit a few basic details through the form."],
  ["We Review the Property", "We evaluate the location, acreage, access, and other relevant factors."],
  ["Discuss Your Options", "If the property is a fit, we contact you to discuss an offer and potential next steps."],
];

const Process = () => (
  <section data-testid="sell-process" className="bg-aspira-sand py-20 lg:py-24">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <Eyebrow>How it works</Eyebrow>
        <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-aspira-ink sm:text-4xl">
          A simple, honest process.
        </h2>
      </Reveal>
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {STEPS.map(([title, body], i) => (
          <Reveal key={title} delay={i * 0.08}>
            <div
              data-testid={`sell-step-${i + 1}`}
              className="h-full rounded-3xl border border-aspira-goldborder/60 bg-white p-8 transition-shadow duration-300 hover:shadow-xl"
            >
              <span className="font-serif text-5xl font-medium text-aspira-gold/40">0{i + 1}</span>
              <h3 className="mt-4 font-serif text-xl font-medium text-aspira-ink">{title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-aspira-body">{body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Sell = () => {
  usePageTitle("Sell Your Land for Cash — Request a Cash Offer | Aspira Property Group");
  return (
    <>
      <section data-testid="sell-hero" className="relative overflow-hidden">
        <div className="pointer-events-none absolute -left-40 top-0 h-[420px] w-[420px] rounded-full bg-aspira-gold/10 blur-3xl" />
        <div className="mx-auto grid max-w-7xl items-start gap-14 px-5 pb-20 pt-16 sm:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:pt-24">
          <div className="lg:sticky lg:top-28">
            <FadeIn><Eyebrow>Sell your land</Eyebrow></FadeIn>
            <h1 className="mt-6 font-serif text-4xl font-medium leading-[1.1] tracking-tight text-aspira-ink sm:text-5xl">
              <HeroLine delay={0.15}>Thinking About</HeroLine>
              <HeroLine delay={0.3}>
                Selling <em className="italic text-aspira-gold">Your Land?</em>
              </HeroLine>
            </h1>
            <FadeIn delay={0.5}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-aspira-body">
                Selling land doesn't have to be complicated. Tell us about your
                property, and we'll review the details, discuss your options, and
                provide an offer if the property fits our acquisition criteria.
                No pressure, no obligation.
              </p>
              <ul className="mt-8 space-y-3 text-sm text-aspira-body">
                {["A straightforward review of your property", "Clear, honest communication throughout", "You decide what happens next"].map((t) => (
                  <li key={t} className="flex items-center gap-3">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-aspira-goldlight text-aspira-gold">
                      <Check size={12} />
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
            </FadeIn>
          </div>
          <FadeIn delay={0.3}>
            <OfferForm />
          </FadeIn>
        </div>
      </section>
      <Process />
    </>
  );
};

export default Sell;
